import type { Interpretation } from "@/lib/api/assistantV2";

/**
 * The plan's self-reported interpretation: how it read the question, how to
 * read the result, and (only when NaNs are present) a NaN guide.
 */
export function InterpretationPanel({
  interpretation,
}: {
  interpretation: Interpretation;
}) {
  const { question_as, result_guide, nan_guide, has_nan, date_range_inclusivity } =
    interpretation;
  if (
    !question_as &&
    !result_guide &&
    !(has_nan && nan_guide) &&
    !date_range_inclusivity
  )
    return null;

  // The plan writes result_guide as a "; "-joined list of per-field clauses
  // (e.g. "`close` is the settlement price; `n` is the count of ..."). Split it
  // into one bullet per field so it reads as a key rather than a run-on blob;
  // fall back to the single clause when there's only one.
  const guideParts = (result_guide ?? "")
    .split(";")
    .map((p) => p.trim())
    .filter(Boolean);

  // Human phrasing for the inclusivity flag (server sends "end_inclusive" /
  // "end_exclusive"); fall back to the raw value for any future variant.
  const inclusivityText =
    date_range_inclusivity === "end_inclusive"
      ? "Date ranges include both endpoints (end-inclusive)."
      : date_range_inclusivity === "end_exclusive"
        ? "Date ranges exclude the end date (end-exclusive)."
        : date_range_inclusivity
          ? `Date range inclusivity: ${date_range_inclusivity}.`
          : null;

  return (
    <div className="rounded-lg border border-violet-100 bg-violet-50/40 px-4 py-3 space-y-2">
      <div className="text-[11px] uppercase tracking-wide text-violet-700">
        Interpretation
      </div>
      {question_as && (
        <div className="text-sm text-slate-800">
          <span className="text-slate-500">Read as: </span>
          {question_as}
        </div>
      )}
      {guideParts.length > 1 ? (
        <ul className="text-sm text-slate-800 list-disc pl-5 space-y-0.5">
          {guideParts.map((part, i) => (
            <li key={i}>{part}</li>
          ))}
        </ul>
      ) : (
        guideParts.length === 1 && (
          <div className="text-sm text-slate-800">{guideParts[0]}</div>
        )
      )}
      {inclusivityText && (
        <div className="text-sm text-slate-800">
          <span className="text-slate-500">Ranges: </span>
          {inclusivityText}
        </div>
      )}
      {has_nan && nan_guide && (
        <div className="text-sm text-amber-800 bg-amber-50/60 border border-amber-100 rounded px-2 py-1.5">
          {nan_guide}
        </div>
      )}
    </div>
  );
}
